import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Row,
  Col,
  Input,
  Button,
  Select,
  DatePicker,
  Table,
  Tag,
  Space,
  Pagination,
  Card,
} from "antd";
import {
  ReloadOutlined,
  FilterOutlined,
  SearchOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Link, useNavigate } from "react-router-dom";
import { getRequest } from "../../util/service";

const { RangePicker } = DatePicker;
const { Option } = Select;

export default function CollapsibleTable({
  projectId,
  projectName,
  owners,
  teamMembers,
  description,
  extraControls, // Prop for the "Tools" dropdown
}) {
  const navigate = useNavigate();

  // ✅ MODIFIED: State for data and loading is now managed inside this component again.
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [distinctUsers, setDistinctUsers] = useState([]);
  
  const [showFilters, setShowFilters] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ✅ MODIFIED: Data fetching logic is moved back into this component.
  const fetchResults = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const resp = await getRequest(`/riva/moleculegenbatches/${projectId}`);
      const data = resp?.data || [];
      const sortedProjects = data.slice().sort(
        (a, b) => new Date(b.submitted_ts) - new Date(a.submitted_ts)
      );
      setAllData(sortedProjects);
      const users = Array.from(new Set(data.map((d) => d.submitted_id).filter(Boolean)));
      setDistinctUsers(users);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  // All filtering and pagination logic remains the same.
  const filtered = useMemo(() => {
    return allData.filter((row) => {
      const txt = searchText.toLowerCase();
      if (txt && ![row.b_record_id, row.submitted_id, row.b_status].some(f => f?.toString().toLowerCase().includes(txt))) return false;
      if (selectedUsers.length && !selectedUsers.includes(row.submitted_id)) return false;
      if (selectedStatuses.length && !selectedStatuses.includes(row.b_status)) return false;
      if (dateRange?.length === 2 && row.submitted_ts) {
        const d = dayjs(row.submitted_ts);
        if (d.isBefore(dateRange[0], "day") || d.isAfter(dateRange[1], "day")) return false;
      }
      return true;
    });
  }, [allData, searchText, selectedUsers, selectedStatuses, dateRange]);

  const displayed = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

 const cols = [
    {
      title: "Experiment",
      dataIndex: "b_record_id",
      key: "batch",
      sorter: (a, b) => a.b_record_id.localeCompare(b.b_record_id),
      render: (text, record) => (
        // ✅ MODIFIED: Pass the parent_batch_id in the navigation state
        <Link
          to={`/project/${projectId}/batch/${record.b_record_id}`}
          state={{ 
            batchStatus: record.b_status, 
            projectName, owners, teamMembers, description,
            parent_batch_id: record.parent_batch_id // Pass the parent ID
          }}
        >
          {record.b_record_id}
        </Link>
      ),
    },
    // ✅ NEW: "Name" column added
    {
      title: "Name",
      dataIndex: "b_name",
      key: "name",
      sorter: (a, b) => (a.b_name || "").localeCompare(b.b_name || ""),
      ellipsis: true,
    },
    // ✅ NEW: "Description" column added
    {
      title: "Description",
      dataIndex: "b_description",
      key: "desc",
      ellipsis: true,
    },
    { title: "Submitted By", dataIndex: "submitted_id", key: "user", sorter: (a, b) => a.submitted_id.localeCompare(b.submitted_id) },
    { title: "Submitted Timestamp", dataIndex: "submitted_ts", key: "ts", sorter: (a, b) => dayjs(a.submitted_ts).unix() - dayjs(b.submitted_ts).unix(), render: (ts) => (ts ? dayjs(ts).format("MMM D, YYYY h:mm A") : "") },
    { title: "Status", dataIndex: "b_status", key: "status", sorter: (a, b) => a.b_status.localeCompare(b.b_status), render: (s) => <Tag color={{"In Progress": "blue", Completed: "green", Terminated: "orange", Failed: "red"}[s] || "gray"}>{s}</Tag> },
  ];

  return (
    <div style={{ padding: "0 24px", marginBottom: 32 }}>
      <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} md={6}><Input placeholder="Search Table" value={searchText} onChange={(e) => setSearchText(e.target.value)} prefix={<SearchOutlined />} suffix={searchText && <ClearOutlined onClick={() => setSearchText("")} />} /></Col>
        <Col flex="auto" />
        <Col>
          <Space>
            {/* The "Reload" button now calls the internal fetchResults function */}
            <Button icon={<ReloadOutlined />} onClick={fetchResults} />
            <Button icon={<FilterOutlined />} onClick={() => setShowFilters(!showFilters)} />
            <Button onClick={() => { setSearchText(""); setSelectedUsers([]); setSelectedStatuses([]); setDateRange([]); }}>Clear All Filters</Button>
            {extraControls}
            <Button type="primary" onClick={() => navigate("/attributes", { state: { projectId, projectName, owners, teamMembers, description } })}>Submit New Generation</Button>
          </Space>
        </Col>
      </Row>

      {showFilters && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}><Select placeholder="Filter by User" mode="multiple" allowClear style={{ width: "100%" }} value={selectedUsers} onChange={setSelectedUsers}>{distinctUsers.map((u) => <Option key={u} value={u}>{u}</Option>)}</Select></Col>
            <Col xs={24} sm={8}><RangePicker style={{ width: "100%" }} value={dateRange} onChange={setDateRange} /></Col>
            <Col xs={24} sm={8}><Select placeholder="Filter by Status" mode="multiple" allowClear style={{ width: "100%" }} value={selectedStatuses} onChange={setSelectedStatuses}>{["In Progress","Failed","Completed","Terminated"].map((s) => <Option key={s} value={s}>{s}</Option>)}</Select></Col>
        </Row>
      )}

      <Card bodyStyle={{ padding: 0 }}>
        {/* The table now uses its own internal loading state */}
        <Table loading={loading} dataSource={displayed} columns={cols} pagination={false} rowKey="b_record_id" />
        <Row justify="end" style={{ padding: "16px" }}>
          <Pagination current={page} pageSize={pageSize} total={filtered.length} showSizeChanger onChange={(p, size) => { setPage(p); setPageSize(size); }} />
        </Row>
      </Card>
    </div>
  );
}
